import { getContentOwnerBinding } from '@/lib/content-owner';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { getAuthEpoch, getAuthUserId, initKeepsakeEpochs } from '@/lib/keepsake/epochs';
import { getSupabase } from '@/lib/supabase';
import type {
  ContentEntityType,
  ContentMutationV1,
  ContentOpReceiptV1,
  OrdinaryContentEnvelopeV1,
} from '@/types/content-lifecycle';
import {
  acknowledgeContentReceipt,
  captureLegacyContentBootstrap,
  CONTENT_LIFECYCLE_GENERATION,
  getContentPartition,
  getDispatchableContentOperations,
  getObsoleteReplayQuarantineCandidate,
  handleContentCasRejection,
  claimContentOperationForDispatch,
  markContentReceiptSuperseded,
  recoverObsoleteReplayQuarantine,
  rebindPendingContentOperations,
  reconcileLegacyContentBootstrap,
  RetiredContentLifecycleContextError,
  stageContentEnvelopes,
  type ContentLifecycleAuthExpectation,
  type ContentLifecycleContext,
} from './store';
import { assertContentOpReceiptV1 } from './contracts';

export const CONTENT_PULL_PAGE_LIMIT = 200;
const CONTENT_SYNC_LOCK_PREFIX = 'watch-him-work-content-sync-v1';

const ALL_CONTENT_ENTITY_TYPES: ReadonlySet<ContentEntityType> = new Set([
  'prayer',
  'bookmark',
  'highlight',
]);

// Fail closed before the browser entry point installs its compile-time feature
// scope. Prayer is the core product record; bookmark/highlight transport is
// opt-in so an early or isolated caller cannot reach a disabled ordinary table.
let runtimeEnabledEntityTypes: ReadonlySet<ContentEntityType> = new Set(['prayer']);

export function configureContentLifecycleFeatureScope(flags: {
  bookmarks: boolean;
  highlights: boolean;
}): void {
  runtimeEnabledEntityTypes = new Set<ContentEntityType>([
    'prayer',
    ...(flags.bookmarks ? ['bookmark' as const] : []),
    ...(flags.highlights ? ['highlight' as const] : []),
  ]);
}

export type ContentApplyResult =
  | { accepted: true; replay: boolean; receipt: ContentOpReceiptV1 }
  | {
      accepted: false;
      reason: 'cas-mismatch';
      currentTip: OrdinaryContentEnvelopeV1 | null;
    };

export type ContentReceiptResult =
  | { found: false }
  | { found: true; receipt: ContentOpReceiptV1 };

export type ContentPullResult = {
  observedHighWater: number;
  envelopes: unknown[];
};

export interface ContentLifecycleTransport {
  apply(expectedOwner: string, mutation: ContentMutationV1): Promise<ContentApplyResult>;
  getReceipt(expectedOwner: string, opId: string): Promise<ContentReceiptResult>;
  pull(expectedOwner: string, afterRevision: number, pageLimit: number): Promise<ContentPullResult>;
}

export type ContentSyncResult = {
  pushed: number;
  replayed: number;
  pulled: number;
  conflicts: number;
  held: boolean;
};

type RpcResult = {
  data: unknown;
  error: { message: string } | null;
};
type RpcRequest = PromiseLike<RpcResult> & {
  abortSignal(signal: AbortSignal): PromiseLike<RpcResult>;
};
type RpcClient = {
  rpc(name: string, args: Record<string, unknown>): RpcRequest;
};

class RetiredContentPullError extends Error {
  constructor() { super('content-pull-context-retired'); }
}

type ContentPullLifetime = {
  ownerId: string;
  signal: AbortSignal;
  isCurrent: () => boolean;
  assertCurrent: () => void;
  retire: () => void;
  dispose: () => void;
};
const activeContentPulls = new Set<ContentPullLifetime>();

function createContentPullLifetime(context: ContentLifecycleContext): ContentPullLifetime {
  const controller = new AbortController();
  const runtimeRevision = ownerRuntimeRevision;
  let retired = false;
  const retire = () => {
    retired = true;
    controller.abort();
  };
  const isCurrent = () => !retired && runtimeRevision === ownerRuntimeRevision && contextIsCurrent(context);
  const lifetime: ContentPullLifetime = {
    ownerId: context.ownerId,
    signal: controller.signal,
    isCurrent,
    assertCurrent: () => {
      if (!isCurrent()) {
        retire();
        throw new RetiredContentPullError();
      }
    },
    retire,
    dispose: () => { activeContentPulls.delete(lifetime); },
  };
  activeContentPulls.add(lifetime);
  if (!isCurrent()) retire();
  return lifetime;
}

function retireChangedContentPulls(ownerId: string | null): void {
  for (const lifetime of activeContentPulls) {
    // The announced owner also works when this listener runs before the epoch
    // listener. Retirement is sticky: A -> B -> A cannot revive an old pull.
    if (ownerId !== lifetime.ownerId || !lifetime.isCurrent()) lifetime.retire();
  }
}

function rpcError(operation: string, message: string): Error {
  return new Error(`content-${operation}-failed: ${message}`);
}

function supabaseTransport(lifetime?: ContentPullLifetime): ContentLifecycleTransport {
  const client = getSupabase() as unknown as RpcClient | null;
  if (!client) throw new Error('content-sync-unconfigured');
  return {
    async apply(expectedOwner, mutation) {
      const { data, error } = await client.rpc('apply_content_mutation_v1', {
        expected_owner: expectedOwner,
        mutation,
      });
      if (error) throw rpcError('apply', error.message);
      return data as ContentApplyResult;
    },
    async getReceipt(expectedOwner, opId) {
      const { data, error } = await client.rpc('get_content_receipt_v1', {
        expected_owner: expectedOwner,
        requested_op_id: opId,
      });
      if (error) throw rpcError('receipt', error.message);
      return data as ContentReceiptResult;
    },
    async pull(expectedOwner, afterRevision, pageLimit) {
      if (!lifetime) throw new Error('content-pull-lifetime-required');
      lifetime.assertCurrent();
      try {
        const { data, error } = await client.rpc('pull_content_changes_v1', {
          expected_owner: expectedOwner,
          after_revision: afterRevision,
          page_limit: pageLimit,
        }).abortSignal(lifetime.signal);
        // The signal survives the SDK's asynchronous credential selection.
        // Keep this fence even if a transport ignores abort or already replied.
        lifetime.assertCurrent();
        if (error) throw rpcError('pull', error.message);
        return data as ContentPullResult;
      } catch (error) {
        if (lifetime.signal.aborted && !lifetime.isCurrent()) {
          throw new RetiredContentPullError();
        }
        throw error;
      }
    },
  };
}

function contextIsCurrent(context: ContentLifecycleContext): boolean {
  return (
    ownerRuntimeEnabled &&
    getAuthUserId() === context.ownerId &&
    getAuthEpoch() === context.authEpoch &&
    CONTENT_LIFECYCLE_GENERATION === context.generation
  );
}

function assertPullResult(value: ContentPullResult, afterRevision: number): void {
  if (
    value === null ||
    typeof value !== 'object' ||
    !Number.isSafeInteger(value.observedHighWater) ||
    value.observedHighWater < afterRevision ||
    !Array.isArray(value.envelopes) ||
    value.envelopes.length > CONTENT_PULL_PAGE_LIMIT
  ) {
    throw new Error('content-pull-response-malformed');
  }
}

async function tryRecoverObsoleteReplayQuarantine(
  context: ContentLifecycleContext,
  transport: ContentLifecycleTransport,
  isCurrent: () => boolean,
): Promise<boolean> {
  if (!isCurrent()) return false;
  const candidate = await getObsoleteReplayQuarantineCandidate(context);
  if (!candidate || !isCurrent()) return false;
  try {
    const [receiptResult, page] = await Promise.all([
      transport.getReceipt(context.ownerId, candidate.opId),
      transport.pull(context.ownerId, candidate.contentRevision - 1, 1),
    ]);
    assertPullResult(page, candidate.contentRevision - 1);
    if (
      !isCurrent() ||
      !receiptResult.found ||
      page.observedHighWater < candidate.frontier ||
      page.envelopes.length !== 1
    ) {
      return false;
    }
    return recoverObsoleteReplayQuarantine(
      context,
      candidate,
      receiptResult.receipt,
      page.envelopes[0],
    );
  } catch {
    // A quarantine is fail-closed. Network, receipt, or proof failure leaves
    // every byte and the latch untouched for a later authenticated retry.
    return false;
  }
}

async function pullToHighWater(
  context: ContentLifecycleContext,
  transport: ContentLifecycleTransport,
  isCurrent: () => boolean,
  enabledEntityTypes: ReadonlySet<ContentEntityType>,
): Promise<{ pulled: number; held: boolean }> {
  let pulled = 0;
  let recoveryAttempted = false;
  while (true) {
    if (!isCurrent()) return { pulled, held: true };
    const before = await getContentPartition(context.ownerId);
    if (!isCurrent()) return { pulled, held: true };
    if (before.quarantineReason) {
      if (!recoveryAttempted) {
        recoveryAttempted = true;
        if (await tryRecoverObsoleteReplayQuarantine(context, transport, isCurrent)) continue;
      }
      return { pulled, held: true };
    }
    if (before.heldReason === 'unknown-content-protocol') {
      return { pulled, held: true };
    }
    let page: ContentPullResult;
    try {
      page = await transport.pull(
        context.ownerId,
        before.frontier,
        CONTENT_PULL_PAGE_LIMIT,
      );
    } catch (error) {
      if (error instanceof RetiredContentPullError && !isCurrent()) return { pulled, held: true };
      throw error;
    }
    if (!isCurrent()) return { pulled, held: true };
    assertPullResult(page, before.frontier);
    const after = await stageContentEnvelopes(
      context,
      page.envelopes,
      page.observedHighWater,
      new Date().toISOString(),
      enabledEntityTypes,
    );
    pulled += page.envelopes.length;
    if (!isCurrent()) return { pulled, held: true };
    if (after.quarantineReason) continue;
    if (after.heldReason) return { pulled, held: true };
    if (after.frontier >= page.observedHighWater) return { pulled, held: false };
    if (after.frontier <= before.frontier) return { pulled, held: true };
  }
}

async function acceptReceipt(
  context: ContentLifecycleContext,
  receipt: ContentOpReceiptV1,
  isCurrent: () => boolean,
): Promise<'acked' | 'superseded' | 'ignored'> {
  if (!isCurrent()) return 'ignored';
  if (await acknowledgeContentReceipt(context, receipt)) return 'acked';
  if (await markContentReceiptSuperseded(context, receipt)) return 'superseded';
  return 'ignored';
}

export async function runContentLifecycleSync(
  context: ContentLifecycleContext,
  transport: ContentLifecycleTransport,
  isCurrent: () => boolean = () => true,
  enabledEntityTypes: ReadonlySet<ContentEntityType> = ALL_CONTENT_ENTITY_TYPES,
): Promise<ContentSyncResult> {
  const legacyBootstrap = await captureLegacyContentBootstrap(context, enabledEntityTypes);
  const adopted = await rebindPendingContentOperations(context, enabledEntityTypes);
  const firstPull = await pullToHighWater(
    context,
    transport,
    isCurrent,
    enabledEntityTypes,
  );
  const result: ContentSyncResult = {
    pushed: 0,
    replayed: 0,
    pulled: firstPull.pulled,
    conflicts: 0,
    held: firstPull.held || adopted.held > 0,
  };
  if (result.held || !isCurrent()) return result;
  await reconcileLegacyContentBootstrap(context, legacyBootstrap);

  while (true) {
    const operations = await getDispatchableContentOperations(context, enabledEntityTypes);
    if (operations.length === 0) break;
    for (const operation of operations) {
      if (!isCurrent()) return { ...result, held: true };

    // Receipt-first retry recovery: a lost apply response is resolved without
    // issuing the mutation again. An exact late receipt never clears a newer
    // successor because acceptReceipt uses the strict durable tuple verifier.
      const prior = await transport.getReceipt(context.ownerId, operation.opId);
      if (prior.found) {
        const accepted = await acceptReceipt(context, prior.receipt, isCurrent);
        if (accepted === 'ignored' || !isCurrent()) return { ...result, held: true };
        result.replayed += 1;
        await stageContentEnvelopes(
          context,
          [prior.receipt.envelope],
          Math.max(
            (await getContentPartition(context.ownerId)).lastObservedHighWater,
            prior.receipt.envelope.contentRevision,
          ),
          new Date().toISOString(),
          enabledEntityTypes,
        );
        continue;
      }

      const claim = await claimContentOperationForDispatch(context, operation);
      if (claim.kind === 'superseded') continue;
      const inFlight = claim.operation;
      if (!isCurrent()) return { ...result, held: true };
      const applied = await transport.apply(context.ownerId, inFlight.mutation);
      if (!isCurrent()) return { ...result, held: true };
      if (applied.accepted) {
        const accepted = await acceptReceipt(context, applied.receipt, isCurrent);
        if (accepted === 'ignored' || !isCurrent()) return { ...result, held: true };
        result.pushed += 1;
        if (applied.replay) result.replayed += 1;
        await stageContentEnvelopes(
          context,
          [applied.receipt.envelope],
          Math.max(
            (await getContentPartition(context.ownerId)).lastObservedHighWater,
            applied.receipt.envelope.contentRevision,
          ),
          new Date().toISOString(),
          enabledEntityTypes,
        );
      } else if (applied.currentTip) {
        const outcome = await handleContentCasRejection(
          context,
          inFlight.tuple,
          applied.currentTip,
          new Date().toISOString(),
        );
        if (outcome !== 'ignored') result.conflicts += 1;
      } else {
        throw new Error('content-cas-response-missing-current-tip');
      }
    }
  }

  const finalPull = await pullToHighWater(
    context,
    transport,
    isCurrent,
    enabledEntityTypes,
  );
  result.pulled += finalPull.pulled;
  result.held = finalPull.held;
  return result;
}

type ContentSyncFlight = {
  promise: Promise<ContentSyncResult>;
  requestedAgain: boolean;
};
const inFlightByOwnerContext = new Map<string, ContentSyncFlight>();

/** Read-only: a sync pass is running in this tab (app-update safety check). */
export function isContentSyncInFlight(): boolean {
  return inFlightByOwnerContext.size > 0;
}

export async function runContentSyncBrowserExclusive<T>(
  ownerId: string,
  isCurrent: () => boolean,
  runner: () => Promise<T>,
  staleResult: () => T,
): Promise<T> {
  const runIfCurrent = () => (isCurrent() ? runner() : Promise.resolve(staleResult()));
  if (typeof navigator === 'undefined') return runIfCurrent();
  if (!navigator.locks) throw new Error('content-sync-lock-unavailable');
  return navigator.locks.request(
    `${CONTENT_SYNC_LOCK_PREFIX}:${ownerId}`,
    { mode: 'exclusive' },
    runIfCurrent,
  );
}

// Session-local aggregate only: no owner, content, exception or transport data.
const syncDiagnostics = { passErrorCount: 0, lastReason: null as 'pass-error' | null };
export function readContentSyncDiagnostics(): Readonly<typeof syncDiagnostics> {
  return { ...syncDiagnostics };
}

export function runContentSyncSingleFlight(
  key: string,
  runner: () => Promise<ContentSyncResult>,
): Promise<ContentSyncResult> {
  const existing = inFlightByOwnerContext.get(key);
  if (existing) {
    // A durable save may arrive after the active pass found an empty outbox,
    // while its final pull is still awaiting the network. Joining its promise
    // alone would lose that wake-up until a later reconnect.
    existing.requestedAgain = true;
    return existing.promise;
  }
  let resolve!: (result: ContentSyncResult) => void;
  let reject!: (reason: unknown) => void;
  const promise = new Promise<ContentSyncResult>((onResolve, onReject) => {
    resolve = onResolve;
    reject = onReject;
  });
  const flight: ContentSyncFlight = { promise, requestedAgain: false };
  // Publish before invoking runner so even a synchronous reentrant request
  // joins this flight. Each pass still obtains the browser lock and rechecks
  // the captured owner/auth epoch/generation through the original runner.
  inFlightByOwnerContext.set(key, flight);
  void (async () => {
    const total: ContentSyncResult = {
      pushed: 0, replayed: 0, pulled: 0, conflicts: 0, held: false,
    };
    try {
      do {
        flight.requestedAgain = false;
        const result = await runner();
        total.pushed += result.pushed;
        total.replayed += result.replayed;
        total.pulled += result.pulled;
        total.conflicts += result.conflicts;
        total.held = result.held;
        // Only an explicit request during the preceding pass schedules one
        // more pass. Failures do not retry themselves or clear durable work.
      } while (flight.requestedAgain);
      return total;
    } catch (error) {
      syncDiagnostics.passErrorCount = Math.min(65535, syncDiagnostics.passErrorCount + 1);
      syncDiagnostics.lastReason = 'pass-error';
      try { console.error('[Adia] content-sync-pass-error', syncDiagnostics.passErrorCount); } catch { /* Diagnostics cannot replace the failure. */ }
      throw error;
    } finally {
      if (inFlightByOwnerContext.get(key) === flight) inFlightByOwnerContext.delete(key);
    }
  })().then(resolve, reject);
  return promise;
}

export async function syncContentLifecycle(
  transport?: ContentLifecycleTransport,
): Promise<ContentSyncResult> {
  if (isCoreReadOnly()) {
    return { pushed: 0, replayed: 0, pulled: 0, conflicts: 0, held: false };
  }
  const context = await getCurrentContentLifecycleContext();
  const enabledEntityTypes = new Set(runtimeEnabledEntityTypes);
  const scopeKey = [...enabledEntityTypes].sort().join(',');
  const key = `${context.ownerId}:${context.authEpoch}:${context.generation}:${scopeKey}`;
  return runContentSyncSingleFlight(key, () =>
    runContentSyncBrowserExclusive(
      context.ownerId,
      () => contextIsCurrent(context),
      async () => {
        const lifetime = createContentPullLifetime(context);
        try {
          return await runContentLifecycleSync(
            context,
            transport ?? supabaseTransport(lifetime),
            lifetime.isCurrent,
            enabledEntityTypes,
          );
        } finally {
          lifetime.dispose();
        }
      },
      () => ({
          pushed: 0,
          replayed: 0,
          pulled: 0,
          conflicts: 0,
          held: true,
      }),
    ),
  );
}

export async function getCurrentContentLifecycleContext(
  expected?: ContentLifecycleAuthExpectation,
): Promise<ContentLifecycleContext> {
  const ownerId = getAuthUserId();
  const authEpoch = getAuthEpoch();
  if (!ownerId) throw new Error('content-sync-not-authenticated');
  if (
    expected &&
    (ownerId !== expected.ownerId || authEpoch !== expected.authEpoch)
  ) {
    throw new Error('content-lifecycle-context-stale');
  }
  if ((await getContentOwnerBinding()) !== ownerId) {
    throw new Error('content-sync-owner-binding-mismatch');
  }
  // The binding read is asynchronous. Capture both auth values before it and
  // recheck them after it so A -> B -> A cannot be normalized into a fresh
  // context for work authored under the earlier A epoch.
  if (getAuthUserId() !== ownerId || getAuthEpoch() !== authEpoch) {
    throw new Error('content-lifecycle-context-stale');
  }
  return {
    ownerId,
    authEpoch,
    generation: CONTENT_LIFECYCLE_GENERATION,
  };
}

/**
 * Read an exact authoritative receipt without mutating local lifecycle state.
 * Text-only browser recovery has no server audio registry to perform this
 * proof, so its local recovery row may be released only after this check.
 */
export async function hasAuthoritativeContentReceipt(input: {
  ownerId: string;
  opId: string;
  entityType: ContentEntityType;
  entityId: string;
}): Promise<boolean> {
  const context = await getCurrentContentLifecycleContext();
  if (context.ownerId !== input.ownerId || !contextIsCurrent(context)) return false;
  const result = await supabaseTransport().getReceipt(input.ownerId, input.opId);
  if (!result.found || !contextIsCurrent(context)) return false;
  try {
    await assertContentOpReceiptV1(result.receipt, input.ownerId);
  } catch {
    return false;
  }
  const envelope = result.receipt.envelope;
  return contextIsCurrent(context) &&
    envelope.opId === input.opId &&
    envelope.entityType === input.entityType &&
    envelope.entityId === input.entityId;
}

let initialized = false;
let requestInitializedSync: (() => void) | null = null;
let ownerRuntimeEnabled = false;
let ownerRuntimeRevision = 0;

/** Enable only while the React owner gate is currently passed. */
export function setContentLifecycleOwnerRuntimeEnabled(enabled: boolean): void {
  if (enabled !== ownerRuntimeEnabled) ownerRuntimeRevision++;
  ownerRuntimeEnabled = enabled;
  if (!enabled) for (const lifetime of activeContentPulls) lifetime.retire();
}

/**
 * Restart the normal auth-bound bootstrap after an empty profile binds.
 * OwnerScopedRuntime installs this runner only after OwnerGate passes, then
 * requests the first pull explicitly. A request made before installation is
 * harmless because the runtime always issues its own request after init.
 */
export function requestContentLifecycleBootstrapSync(): void {
  requestInitializedSync?.();
}

export function initContentLifecycleSync(onSettled: () => void | Promise<void>): void {
  if (isCoreReadOnly() || initialized || typeof window === 'undefined') return;
  initialized = true;
  // Ordinary content uses the same synchronous A->B->A auth epoch. It must
  // be initialized even while keepsakes themselves remain feature-disabled.
  initKeepsakeEpochs();
  const run = (attempt = 0, expectedSessionOwner?: string) => {
    if (!ownerRuntimeEnabled || !navigator.onLine) return;
    const ownerId = getAuthUserId();
    if (ownerId === undefined) {
      if (attempt < 20) {
        setTimeout(() => run(attempt + 1, expectedSessionOwner), 50);
      }
      return;
    }
    if (!ownerId || (expectedSessionOwner && ownerId !== expectedSessionOwner)) return;
    const authEpoch = getAuthEpoch();
    const runtimeRevision = ownerRuntimeRevision;
    const isCurrent = () => ownerRuntimeEnabled &&
      runtimeRevision === ownerRuntimeRevision &&
      getAuthUserId() === ownerId && getAuthEpoch() === authEpoch;
    return getContentOwnerBinding()
      .then(async (binding) => {
        if (binding !== ownerId || !isCurrent()) return;
        try {
          await syncContentLifecycle();
        } catch (error) {
          if (
            error instanceof RetiredContentLifecycleContextError &&
            error.ownerId === ownerId &&
            error.authEpoch === authEpoch &&
            error.generation === CONTENT_LIFECYCLE_GENERATION &&
            (getAuthUserId() !== ownerId || getAuthEpoch() !== authEpoch)
          ) return;
          throw error;
        }
        if (!isCurrent()) return;
        await onSettled();
      })
      .catch(() => {
        try { console.error('[Adia] content-sync-runtime-error'); } catch { /* Best effort only. */ }
      });
  };
  requestInitializedSync = () => run();
  window.addEventListener('online', () => run());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') run();
  });
  const client = getSupabase();
  client?.auth.onAuthStateChange((event, session) => {
    retireChangedContentPulls(session?.user.id ?? null);
    if (
      session &&
      (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')
    ) {
      run(0, session.user.id);
    }
  });
}

export function isPrayerReadyForContentSync(prayer: { pendingTranscriptionId?: string }): boolean {
  return !prayer.pendingTranscriptionId;
}
